import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { PortalUsuarioEmpresaService } from '../../../../services/portalUsuarioEmpresa.service';
import { IGetEmpresaByPortalUsuarioId } from '../../../../shared/interfaces/configuracion';
import { DropdownModule } from 'primeng/dropdown'
import { FormGroupErrorMessageComponent, noWhitespaceValidator, Utils } from 'lib-base';
import { firstValueFrom, forkJoin } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { AuthenticationManagerService } from '../../../../services/authentication-manager.service';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { FileUpload, FileUploadHandlerEvent, FileUploadModule } from 'primeng/fileupload';
import { PortalUsuarioEmpresaDto } from '../../../../shared/interfaces/seguridad';
import {IconFieldModule} from "primeng/iconfield";
import {InputIconModule} from "primeng/inputicon";

@Component({
  selector: 'app-efectuar-tramite-empresa-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, DropdownModule, FormGroupErrorMessageComponent, ButtonModule, InputTextModule, InputNumberModule, FileUploadModule, IconFieldModule, InputIconModule],
  templateUrl: './efectuar-tramite-empresa-form.component.html',
  styleUrl: './efectuar-tramite-empresa-form.component.scss'
})
export class EfectuarTramiteEmpresaFormComponent implements OnInit {

  index: number = 0;

  empresa: IGetEmpresaByPortalUsuarioId[] = [];

  empresaSelect !: FormGroup;
  empresaCreate !: FormGroup;

  translations: any = {};

  archivo?: File = undefined;
  readonly uploadFileSize: number = 1024 * 1024
  invalidSize = false;
  requieredFile = false;

  constructor(private readonly _pue: PortalUsuarioEmpresaService, private readonly _fb: FormBuilder, private readonly _t: TranslateService, private readonly _ms: MessageService,
    private readonly _am: AuthenticationManagerService
  ) {
    this.configureEmpresaSelectForm();
    this.configureEmpresaCreateForm();
    this.getEmpresaByPortalUsuarioId();
  }

  async ngOnInit() {
    this.translations = await firstValueFrom(forkJoin({
      empresa: this._t.get('common.empresa'),
      seleccionar: this._t.get('common.seleccionar'),
      registrar: this._t.get('components.app-efectuar-tramite-empresa-form.registrar'),
      warnSummary: this._t.get('form.msg.warn-summary'),
      completeCampos: this._t.get('form.msg.complete-campos'),
      nombre: this._t.get('components.app-efectuar-tramite-empresa-form.nombre'),
      nit: this._t.get('components.app-efectuar-tramite-empresa-form.nit'),
      direccion: this._t.get('common.direccion'),
      telefono: this._t.get('common.telefono'),
      correo: this._t.get('common.correo'),
      documento: this._t.get('common.documento'),
      invalidSize: this._t.get('form.msg.invalid-size'),
      campoRequerido: this._t.get('form.msg.campo-requerido'),
      archivo: this._t.get('common.archivo'),
      wasUploaded: this._t.get('form.msg.was-uploaded'),
      agregarDocumento: this._t.get('form.button.agregar-documento'),
      registrarBtn: this._t.get('form.button.registrar'),
      limpiar: this._t.get('form.button.limpiar'),
      empresaRegistrada: this._t.get('components.app-efectuar-tramite-empresa-form.empresa-registrada'),
      empresaRegistradaMsg: this._t.get('components.app-efectuar-tramite-empresa-form.empresa-registrada-msg'),
    }));

  }

  configureEmpresaSelectForm() {
    this.empresaSelect = this._fb.group({
      empresaSelectedId: [null, [Validators.required]]
    });
  }

  configureEmpresaCreateForm() {
    this.empresaCreate = this._fb.group({
      nombre: [null, [Validators.required, noWhitespaceValidator, Validators.maxLength(255)]],
      nit: [null, [Validators.required, noWhitespaceValidator, Validators.maxLength(50)]],
      direccion: [null, [Validators.required, noWhitespaceValidator, Validators.maxLength(255)]],
      telefono: [null, [Validators.required, noWhitespaceValidator, Validators.maxLength(20)]],
      correo: [null, [Validators.required, noWhitespaceValidator, Validators.email, Validators.maxLength(100)]],
    });
  }

  onTabChange(index: number) {
    this.index = index;
  }

  uploadHandler(event: FileUploadHandlerEvent, fileUpload: FileUpload) {
    const file = event.files[0];
    this.invalidSize = false;
    //validate file size
    if (file.size > (1024 * 1024)) {
      this.invalidSize = true;
      fileUpload.clear();
      return;
    }
    this.archivo = file;
    fileUpload.clear();
    this.requieredFile = false;
  }

  removeFile() {
    this.archivo = undefined;
    this.invalidSize = false;
  }

  onLimpiarDatos() {
    this.empresaCreate.reset();
    this.removeFile();
  }

  onEmpresaCreateSubmit() {
    const missingFile = this.archivo === undefined;
    if (this.empresaCreate.invalid || missingFile) {
      Utils.markAllFormControlsAsTouched(this.empresaCreate);
      if (missingFile) {
        this.requieredFile = true;
      }
      this._ms.add({ severity: 'warn', summary: this.translations.warnSummary, detail: this.translations.completeCampos });
      return;
    }

    let request: PortalUsuarioEmpresaDto = {
      nombre: this.empresaCreate.get('nombre')?.value,
      nit: this.empresaCreate.get('nit')?.value,
      direccion: this.empresaCreate.get('direccion')?.value,
      telefono: this.empresaCreate.get('telefono')?.value,
      correo: this.empresaCreate.get('correo')?.value,
    }

    this.registrarEmpresa(request);
  }

  onEmpresaSelectSubmit() {
    if (this.empresaSelect.invalid) {
      Utils.markAllFormControlsAsTouched(this.empresaSelect);
      this._ms.add({ severity: 'warn', summary: this.translations.warnSummary, detail: this.translations.completeCampos });
      return;
    }

    this._am.onMenuClose(this.empresaSelect.get('empresaSelectedId')?.value);
  }

  //#region API Calls
  private registrarEmpresa(request: PortalUsuarioEmpresaDto) {
    if (this.archivo === undefined) {
      this.requieredFile = true;
      return;
    }

    this._pue.registrarEmpresa(request, this.archivo).subscribe({
      next: (response: number) => {
        this._ms.add({ severity: 'success', summary: this.translations.empresaRegistrada, detail: this.translations.empresaRegistradaMsg });
        this.onLimpiarDatos();
        this._am.onMenuClose(response);
      }
    });
  }

  private getEmpresaByPortalUsuarioId() {
    this._pue.getEmpresaByPortalUsuarioId().subscribe({
      next: (response: IGetEmpresaByPortalUsuarioId[]) => {
        this.empresa = response;
      }
    });
  }
  //#endregion
}
